import { FACING_MODES, IMAGE_TYPES, NO_CONSTRAINTS, SUPPORTED_FACING_MODES } from "./constants";
import { getDataUri, getImageSize, getIsIOS, hasConstraints } from "./helper";

export type CameraResolution = Pick<MediaTrackConstraints, "width" | "height">;

export interface DataUriConfig {
	sizeFactor: number;
	imageType: string;
	imageCompression: number | null;
	isImageMirror: boolean;
}

type CameraMediaDevices = MediaDevices & { getUserMedia(constraints: MediaStreamConstraints): Promise<MediaStream> };
type LegacyGetUserMedia = (
	constraints: MediaStreamConstraints,
	success: (stream: MediaStream) => void,
	error: (error: DOMException) => void,
) => void;

export class MediaServices {
	static getDataUri(videoElement: HTMLVideoElement, config: DataUriConfig) {
		const { imageWidth, imageHeight } = getImageSize(
			videoElement.videoWidth,
			videoElement.videoHeight,
			config.sizeFactor,
		);
		const canvas = document.createElement("canvas");
		canvas.width = imageWidth;
		canvas.height = imageHeight;
		const context = canvas.getContext("2d");
		if (!context) throw new Error("Unable to create a canvas context");
		if (config.isImageMirror) context.setTransform(-1, 0, 0, 1, canvas.width, 0);
		context.drawImage(videoElement, 0, 0, imageWidth, imageHeight);
		return getDataUri(canvas, config.imageType, config.imageCompression);
	}

	static getWindowURL() {
		return window.URL;
	}

	static getNavigatorMediaDevices(): CameraMediaDevices | null {
		if (navigator.mediaDevices?.getUserMedia) return navigator.mediaDevices;
		const legacyNavigator = navigator as Navigator & {
			mozGetUserMedia?: LegacyGetUserMedia;
			webkitGetUserMedia?: LegacyGetUserMedia;
		};
		const legacyGetUserMedia = legacyNavigator.mozGetUserMedia ?? legacyNavigator.webkitGetUserMedia;
		if (!legacyGetUserMedia) return null;
		return {
			...navigator.mediaDevices,
			getUserMedia: (constraints) =>
				new Promise<MediaStream>((resolve, reject) =>
					legacyGetUserMedia.call(navigator, constraints, resolve, reject),
				),
		};
	}

	static isSupportedFacingMode() {
		return this.getNavigatorMediaDevices()?.getSupportedConstraints().facingMode ?? false;
	}

	static getIdealConstraints(idealCameraDevice?: string, idealResolution?: CameraResolution): MediaStreamConstraints {
		if (!hasConstraints(idealCameraDevice, idealResolution)) return NO_CONSTRAINTS;
		const supported = this.getNavigatorMediaDevices()?.getSupportedConstraints();
		if (!supported?.width || !supported.height) return NO_CONSTRAINTS;
		const video: MediaTrackConstraints = {
			facingMode:
				idealCameraDevice &&
				SUPPORTED_FACING_MODES.includes(idealCameraDevice as (typeof SUPPORTED_FACING_MODES)[number])
					? idealCameraDevice
					: undefined,
			deviceId:
				idealCameraDevice &&
				!SUPPORTED_FACING_MODES.includes(idealCameraDevice as (typeof SUPPORTED_FACING_MODES)[number])
					? { exact: idealCameraDevice }
					: undefined,
			width: idealResolution?.width,
			height: idealResolution?.height,
		};
		return { audio: false, video };
	}

	static getMaxResolutionConstraints(idealCameraDevice = "", attempt = 0): MediaStreamConstraints | null {
		const advanced = [640, 800, 900, 1024, 1080, 1280, 1920, 2560, 3840].map((min) => ({
			width: { min },
		})) as MediaTrackConstraintSet[];
		const constraints = this.getIdealConstraints(idealCameraDevice, {});
		if (attempt === 0 && getIsIOS()) {
			return this.getIdealConstraints(idealCameraDevice, {
				width: { min: 640, ideal: 3840 },
				height: { min: 480, ideal: 2160 },
			});
		}
		if (attempt >= advanced.length) return this.getIdealConstraints(idealCameraDevice, {});
		if (constraints.video === true) constraints.video = {};
		if (typeof constraints.video !== "object") return constraints;
		constraints.video.advanced = advanced.slice(0, -attempt || undefined);
		return constraints;
	}

	static FACING_MODES = FACING_MODES;
	static IMAGE_TYPES = IMAGE_TYPES;
}
